<template>
  <div class="user-menu">
    <template v-if="user">
      <div class="user-avatar-wrapper">
        <button
          class="avatar-trigger"
          :class="{ 'is-open': isOpen }"
          :aria-expanded="isOpen"
          :aria-label="$t('userMenu.openMenu')"
          @click.stop="toggleDropdown"
        >
          <span class="user-avatar">
            <img v-if="user.photoURL" :src="user.photoURL" :alt="user.email || ''">
            <span v-else class="avatar-initial">{{ getInitial(user.email) }}</span>
          </span>
          <span class="presence-dot"></span>
          <span class="trigger-chevron" aria-hidden="true"></span>
        </button>
        <Transition 
          name="v"
          @before-enter="onBeforeEnter"
          @enter="onEnter"
          @leave="onLeave"
        >
          <div class="menu-dropdown" v-if="isOpen" @click.stop>
            <div class="account-header">
              <div class="account-avatar">
                <img v-if="user.photoURL" :src="user.photoURL" :alt="user.email || ''">
                <span v-else>{{ getInitial(user.email) }}</span>
              </div>
              <div class="account-copy">
                <div class="account-name">{{ displayName }}</div>
                <div class="user-email">{{ user.email }}</div>
              </div>
              <span v-if="userDataReady" class="plan-badge">{{ planName }}</span>
              <span v-else class="plan-badge plan-badge-loading" aria-hidden="true"></span>
            </div>

            <div v-if="userDataReady" class="usage-card">
              <div class="usage-bucket">
                <div class="usage-heading">
                  <span>{{ $t('userMenu.monthlyBalance') }}</span>
                  <span class="usage-value">
                    <strong>{{ monthlyRemainingUsage }}</strong>
                    <small>/ {{ currentMaxUsage }}</small>
                  </span>
                </div>
                <div class="usage-progress">
                  <span
                    class="usage-bar"
                    :class="{ low: usagePercentage < 20 }"
                    :style="{ width: `${usagePercentage}%` }"
                  ></span>
                </div>
              </div>
              <div v-if="(isPremium || isPro) && premiumExpiry" class="premium-expiry">
                {{ $t('premium.premiumExpiry', { date: formatExpiryDate(premiumExpiry) }) }}
              </div>
              <div v-if="showPurchasedCredits" class="usage-bucket permanent-credits">
                <div class="usage-heading">
                  <span>{{ $t('userMenu.permanentBalance') }}</span>
                  <span class="usage-value">
                    <strong>{{ purchasedCredits }}</strong>
                    <small>/ {{ purchasedCreditsTotal }}</small>
                  </span>
                </div>
                <div class="usage-progress">
                  <span
                    class="usage-bar permanent-bar"
                    :style="{ width: `${purchasedPercentage}%` }"
                  ></span>
                </div>
              </div>
            </div>
            <div v-else class="usage-card usage-card-loading" aria-hidden="true">
              <span></span>
              <span></span>
            </div>

            <div class="menu-content">
              <button
                v-if="userDataReady && !isPremium && !isPro"
                @click.stop="handleUpgrade"
                class="menu-action upgrade-action"
              >
                <span class="action-icon icon-sparkles"></span>
                <span class="action-copy">
                  <strong>{{ $t('userMenu.upgradeTitle') }}</strong>
                  <small>{{ $t('userMenu.upgradeDescription') }}</small>
                </span>
                <span class="action-arrow" aria-hidden="true"></span>
              </button>
              <button
                v-if="canManageSubscription"
                @click.stop="handleBilling"
                class="menu-action"
              >
                <span>{{ $t('userMenu.manageBilling') }}</span>
                <span class="action-arrow" aria-hidden="true"></span>
              </button>
              <button
                type="button"
                class="menu-action"
                @click.stop="handleProfile"
              >
                <span class="action-icon icon-settings" aria-hidden="true"></span>
                <span class="menu-label">{{ $t('userMenu.profile') }}</span>
                <span class="action-arrow" aria-hidden="true"></span>
              </button>
              <button
                type="button"
                class="menu-action"
                @click.stop="handleTutorial"
              >
                <span class="action-icon icon-tutorial" aria-hidden="true"></span>
                <span class="menu-label">{{ $t('userMenu.tutorial') }}</span>
                <span class="action-arrow" aria-hidden="true"></span>
              </button>
              <button
                type="button"
                class="menu-action"
                @click.stop="handleFeedback"
              >
                <span class="action-icon icon-feedback" aria-hidden="true"></span>
                <span class="menu-label">{{ $t('userMenu.feedback') }}</span>
                <span class="action-arrow" aria-hidden="true"></span>
              </button>
              <button
                type="button"
                class="menu-action"
                @click.stop="handleInvite"
              >
                <span class="action-icon icon-gift referral-icon"></span>
                <span class="menu-label">{{ $t('userMenu.inviteTitle') }}</span>
                <span class="action-arrow" aria-hidden="true"></span>
              </button>
              <button
                type="button"
                class="menu-action"
                @click.stop="handleGiftCode"
              >
                <span class="action-icon icon-gift" aria-hidden="true"></span>
                <span class="menu-label">{{ $t('userMenu.redeemGiftCode') }}</span>
                <span class="action-arrow" aria-hidden="true"></span>
              </button>
              <div class="account-actions">
                <button
                  v-if="userDataReady"
                  type="button"
                  class="compact-action credits-action"
                  @click.stop="handleBuyCredits"
                >
                  {{ $t('userMenu.buyCreditsTitle') }}
                </button>
                <button
                  type="button"
                  class="compact-action logout-action"
                  @click.stop="handleLogout"
                >
                  <span class="action-icon icon-logout"></span>
                  <span>{{ $t('navbar.logout') }}</span>
                </button>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </template>
    <GiftCodeDialog v-if="user" :show="showGiftCode" @close="showGiftCode = false" />
    <template v-else>
      <button @click="$emit('login')" class="btn btn-primary">
        {{ $t('navbar.login') }}
      </button>
      <button @click="$emit('signup')" class="btn btn-secondary">
        {{ $t('navbar.signup') }}
      </button>
    </template>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import { apiPost } from '@/composables/useApi'
import { useViews } from '@/composables/views'
import { demoLessonProps } from '@/composables/demoLesson'
import GiftCodeDialog from '@/components/referral/GiftCodeDialog.vue'
import { openFeedback } from '@/composables/useFeedback'

export default defineComponent({
  name: 'UserMenu',
  components: { GiftCodeDialog },
  props: {
    user: Object,
    userDataReady: {
      type: Boolean,
      default: false
    },
    isPremium: {
      type: Boolean,
      default: false
    },
    isPro: {
      type: Boolean,
      default: false
    },
    remainingUsage: {
      type: Number,
      default: 0
    },
    monthlyRemainingUsage: {
      type: Number,
      default: 0
    },
    maxMonthlyUsage: {
      type: Number,
      default: 200
    },
    purchasedCredits: {
      type: Number,
      default: 0
    },
    purchasedCreditsTotal: {
      type: Number,
      default: 0
    },
    canManageSubscription: {
      type: Boolean,
      default: false
    },
    premiumType: {
      type: String,
      default: ''
    },
    premiumExpiry: {
      type: String,
      default: ''
    }
  },
  computed: {
    currentMaxUsage() {
      return this.maxMonthlyUsage
    },
    usagePercentage() {
      if (!this.currentMaxUsage) return 0
      return Math.min((this.monthlyRemainingUsage / this.currentMaxUsage) * 100, 100)
    },
    purchasedPercentage() {
      if (!this.purchasedCreditsTotal) return 0
      return Math.min((this.purchasedCredits / this.purchasedCreditsTotal) * 100, 100)
    },
    showPurchasedCredits() {
      return this.purchasedCreditsTotal > 0 || this.purchasedCredits > 0
    },
    planName() {
      return this.isPremium
        ? this.$t('recentFiles.premiumUser')
        : this.isPro
          ? this.$t('recentFiles.proUser')
          : this.$t('recentFiles.freeUser')
    },
    displayName() {
      return this.user?.displayName || this.$t('userMenu.account')
    }
  },
  data() {
    return {
      isOpen: false,
      showGiftCode: false
    }
  },
  methods: {
    getInitial(email) {
      return email ? email.charAt(0).toUpperCase() : '?'
    },
    toggleDropdown(event) {
      event.stopPropagation()
      this.isOpen = !this.isOpen
    },
    handleLogout(event) {
      event.stopPropagation()
      this.isOpen = false
      this.$emit('logout')
    },
    handleUpgrade(event) {
      event.stopPropagation()
      this.isOpen = false
      this.$emit('upgrade')
    },
    handleProfile() {
      this.isOpen = false
      useViews().handleViewChange('Profile')
    },
    handleGiftCode() {
      this.isOpen = false
      this.showGiftCode = true
    },
    handleFeedback() {
      this.isOpen = false
      openFeedback()
    },
    handleInvite() {
      this.isOpen = false
      useViews().handleViewChange('Referral')
    },
    // 打开示范素材并从头开始操作引导
    handleTutorial() {
      this.isOpen = false
      useViews().handleViewChange('AudioPlayer', {
        ...demoLessonProps({ audioName: this.$t('audioPlayer.demoVideoName'), returnView: 'Material' }),
        tourRequest: Date.now()
      })
    },
    handleBuyCredits(event) {
      event.stopPropagation()
      this.isOpen = false
      this.$emit('upgrade', 'credits')
    },
    async handleBilling(event) {
      event.stopPropagation()
      const { url } = await apiPost('/api/create-billing-portal-session', {})
      window.location.href = url
    },
    handleClickOutside(event) {
      if (this.isOpen && !this.$el.contains(event.target)) {
        this.isOpen = false
      }
    },
    onBeforeEnter(el) {
      el.style.opacity = 0
      el.style.transform = 'translateY(-8px) scale(0.98)'
    },
    onEnter(el, done) {
      setTimeout(() => {
        el.style.opacity = 1
        el.style.transform = 'translateY(0) scale(1)'
        done()
      }, 50)
    },
    onLeave(el, done) {
      el.style.opacity = 0
      el.style.transform = 'translateY(-8px) scale(0.98)'
      setTimeout(done, 180)
    },
    formatExpiryDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleDateString()
    }
  },
  created() {
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
  }
})
</script>

<style scoped>
.user-menu {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.user-avatar-wrapper {
  position: relative;
}

.avatar-trigger {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.32rem;
  height: 38px;
  padding: 2px 8px 2px 2px;
  color: #425466;
  background: rgba(255, 255, 255, 0.46);
  border: 1px solid rgba(23, 107, 77, 0.16);
  border-radius: 999px;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.68),
    0 10px 24px rgba(17, 24, 39, 0.06);
  cursor: pointer;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
}

.avatar-trigger:hover,
.avatar-trigger.is-open {
  background: rgba(255, 255, 255, 0.74);
  border-color: rgba(23, 107, 77, 0.28);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.8),
    0 14px 30px rgba(23, 107, 77, 0.1);
}

.user-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  overflow: hidden;
  color: #ffffff;
  background: linear-gradient(145deg, #43d3d5, #35c7c9 52%, #8bd7bb);
  border: 2px solid rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  box-shadow: 0 0 0 1px rgba(53, 199, 201, 0.22);
}

.user-avatar img,
.account-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-initial {
  font-size: 0.92rem;
  font-weight: 800;
  line-height: 1;
}

.presence-dot {
  position: absolute;
  left: 28px;
  bottom: 2px;
  width: 8px;
  height: 8px;
  background: #35c7c9;
  border: 2px solid #fbfcf7;
  border-radius: 50%;
}

.trigger-chevron {
  width: 6px;
  height: 6px;
  border-right: 1.5px solid currentColor;
  border-bottom: 1.5px solid currentColor;
  transform: rotate(45deg) translateY(-2px);
  transition: transform 0.18s ease;
}

.avatar-trigger.is-open .trigger-chevron {
  transform: rotate(225deg) translate(-1px, -1px);
}

.menu-dropdown {
  position: absolute;
  top: calc(100% + 0.7rem);
  right: 0;
  width: 328px;
  overflow: hidden;
  background:
    linear-gradient(180deg, rgba(251, 252, 247, 0.98), rgba(246, 248, 241, 0.96)),
    linear-gradient(135deg, rgba(23, 107, 77, 0.08), rgba(53, 199, 201, 0.05));
  border: 1px solid rgba(23, 107, 77, 0.16);
  border-radius: 16px;
  box-shadow:
    0 24px 60px rgba(17, 24, 39, 0.14),
    0 1px 0 rgba(255, 255, 255, 0.78) inset;
  z-index: 1000;
  transform-origin: top right;
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.account-header {
  display: grid;
  grid-template-columns: 46px minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.75rem;
  padding: 1.15rem 1.15rem 1rem;
  background:
    linear-gradient(135deg, rgba(236, 253, 245, 0.78), rgba(255, 255, 255, 0.6));
  border-bottom: 1px solid rgba(23, 107, 77, 0.1);
}

.account-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  overflow: hidden;
  color: #ffffff;
  background: linear-gradient(145deg, #43d3d5, #35c7c9 52%, #8bd7bb);
  border: 3px solid rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  box-shadow: 0 8px 20px rgba(53, 199, 201, 0.16);
  font-size: 1rem;
  font-weight: 800;
}

.account-copy {
  min-width: 0;
}

.account-name {
  overflow: hidden;
  color: #0f172a;
  font-size: 0.92rem;
  font-weight: 750;
  line-height: 1.3;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-email {
  margin-top: 0.18rem;
  overflow: hidden;
  color: #7c8ba1;
  font-size: 0.72rem;
  line-height: 1.3;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.plan-badge {
  padding: 0.28rem 0.5rem;
  color: #176b4d;
  background: rgba(236, 253, 245, 0.88);
  border: 1px solid rgba(23, 107, 77, 0.16);
  border-radius: 999px;
  font-size: 0.65rem;
  font-weight: 750;
  white-space: nowrap;
}

.plan-badge-loading {
  width: 3.5rem;
  height: 1.35rem;
  border-color: transparent;
}

.usage-card {
  margin: 0.9rem 1rem;
  padding: 0.9rem;
  background: rgba(255, 255, 255, 0.62);
  border: 1px solid rgba(23, 107, 77, 0.12);
  border-radius: 12px;
}

.plan-badge-loading,
.usage-card-loading span {
  background: linear-gradient(
    90deg,
    rgba(148, 163, 184, 0.12) 25%,
    rgba(148, 163, 184, 0.24) 50%,
    rgba(148, 163, 184, 0.12) 75%
  );
  background-size: 200% 100%;
  animation: user-data-loading 1.25s ease-in-out infinite;
}

.usage-card-loading {
  display: grid;
  gap: 0.75rem;
}

.usage-card-loading span {
  display: block;
  height: 0.8rem;
  border-radius: 999px;
}

.usage-card-loading span:last-child {
  height: 5px;
}

@keyframes user-data-loading {
  from {
    background-position: 200% 0;
  }

  to {
    background-position: -200% 0;
  }
}

.usage-bucket + .usage-bucket {
  margin-top: 0.85rem;
}

.usage-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #64748b;
  font-size: 0.74rem;
}

.usage-value {
  display: flex;
  align-items: baseline;
  gap: 0.2rem;
}

.usage-value strong {
  color: #0f172a;
  font-size: 0.95rem;
  font-weight: 750;
}

.usage-value small {
  color: #94a3b8;
  font-size: 0.68rem;
}

.usage-progress {
  height: 5px;
  margin-top: 0.7rem;
  overflow: hidden;
  background: rgba(23, 107, 77, 0.12);
  border-radius: 999px;
}

.usage-bar {
  display: block;
  height: 100%;
  background: linear-gradient(90deg, #176b4d, #35c7c9);
  border-radius: inherit;
  transition: width 0.25s ease;
}

.usage-bar.low {
  background: #ef4444;
}

.permanent-credits {
  padding-top: 0.8rem;
  border-top: 1px solid rgba(23, 107, 77, 0.1);
}

.permanent-bar {
  background: linear-gradient(90deg, #f59e0b, #facc15);
}

.premium-expiry {
  margin-top: 0.55rem;
  font-size: 0.75rem;
  color: #7c8ba1;
}

.menu-content {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  padding: 0 1rem 1rem;
}

.menu-action {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.7rem;
  min-height: 44px;
  padding: 0.65rem 0.75rem;
  color: #334155;
  background: rgba(255, 255, 255, 0.38);
  border: 1px solid transparent;
  border-radius: 11px;
  font-size: 0.82rem;
  font-weight: 650;
  text-align: left;
  cursor: pointer;
  transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease;
}

.action-icon {
  display: block;
  width: 18px;
  height: 18px;
  flex: 0 0 18px;
  background: currentColor;
  mask-position: center;
  mask-repeat: no-repeat;
  mask-size: contain;
  -webkit-mask-position: center;
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-size: contain;
}

.icon-sparkles {
  mask-image: url('~@/assets/ui-icons/sparkles.svg');
  -webkit-mask-image: url('~@/assets/ui-icons/sparkles.svg');
}

.icon-settings {
  mask-image: url('~@/assets/ui-icons/settings.svg');
  -webkit-mask-image: url('~@/assets/ui-icons/settings.svg');
}

.icon-tutorial {
  mask-image: url('~@/assets/ui-icons/play.svg');
  -webkit-mask-image: url('~@/assets/ui-icons/play.svg');
}

.icon-feedback {
  mask-image: url('~@/assets/ui-icons/feedback.svg');
  -webkit-mask-image: url('~@/assets/ui-icons/feedback.svg');
}

.icon-gift {
  mask-image: url('~@/assets/ui-icons/gift.svg');
  -webkit-mask-image: url('~@/assets/ui-icons/gift.svg');
}

.referral-icon {
  color: var(--signal-reward);
}

.menu-label {
  flex: 1 1 auto;
}

.icon-logout {
  mask-image: url('~@/assets/ui-icons/logout.svg');
  -webkit-mask-image: url('~@/assets/ui-icons/logout.svg');
}

.upgrade-action {
  color: #0f172a;
  background: rgba(236, 253, 245, 0.78);
  border-color: rgba(23, 107, 77, 0.16);
}

.upgrade-action:hover {
  background: rgba(236, 253, 245, 0.96);
  border-color: rgba(23, 107, 77, 0.28);
}

.account-actions {
  display: flex;
  gap: var(--space-2);
}

.compact-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  min-height: 40px;
  padding: 0 var(--space-3);
  color: var(--color-text-muted);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease;
}

.compact-action:focus-visible {
  border-color: var(--signal-active-strong);
  box-shadow: var(--focus-ring-brand);
  outline: none;
}

.credits-action {
  flex: 1;
  color: var(--color-text);
}

.credits-action:hover {
  color: var(--signal-active-strong);
  background: var(--color-surface-muted);
  border-color: var(--color-border-strong);
}

.action-copy {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 0.12rem;
  min-width: 0;
}

.action-copy strong {
  font-size: 0.8rem;
}

.action-copy small {
  overflow: hidden;
  color: #667085;
  font-size: 0.67rem;
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.action-arrow {
  width: 7px;
  height: 7px;
  margin-right: 0.15rem;
  border-top: 1.5px solid rgba(23, 107, 77, 0.55);
  border-right: 1.5px solid rgba(23, 107, 77, 0.55);
  transform: rotate(45deg);
}

.logout-action {
  flex: 0 0 auto;
}

.logout-action:hover {
  color: var(--color-danger);
  background: var(--color-danger-bg);
  border-color: var(--color-danger-border);
}

.v-enter-active,
.v-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
  transform: translateY(-8px) scale(0.98);
}

@media (max-width: 768px) {
  .compact-action {
    min-height: 44px;
  }

  .avatar-trigger {
    height: 38px;
    padding: 2px;
    border: 0;
    background: transparent;
    box-shadow: none;
  }

  .user-avatar {
    width: 34px;
    height: 34px;
  }

  .presence-dot {
    left: 26px;
    bottom: 1px;
  }

  .trigger-chevron {
    display: none;
  }

  .menu-dropdown {
    position: fixed;
    top: 64px;
    right: 12px;
    width: min(328px, calc(100vw - 24px));
  }
}
</style>
